import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import type { FutureMinter, Grantor, Organization } from '../../types/types';

// Query response types
export interface GetGrantorOrgsResponse { getGrantorOrgsFromUserName: Organization[] }
export interface GetAdminsForOrgResponse { getAdminsForOrg: Grantor[] }
export interface GetFutureMintersForOrgResponse { getFutureMintersForOrg: FutureMinter[] }
export interface GetGrantorsForOrgResponse { getGrantorsForOrg: Grantor[] }
export interface GetGrantorsResponse { getGrantorsFromUserName: Grantor[] }
export interface IsAdminResponse { isAdmin: { UserName: string; IsAnAdmin: boolean } }
export interface GetOrgAdminsResponse { getOrgAdminsFromUserName: Grantor[] }
export interface GetOrganizationResponse { getOrganizationFromId: Organization }
export interface GetOrganizationsResponse { getOrganizations: Organization[] }

const createOrganizationMutation = gql`
    mutation createOrganization(
        $OrganizationId: String!,
        $OrganizationName: String!,
        $Address1: String!,
        $Address2: String!,
        $City: String!,
        $State: String!,
        $PostalCode: String!,
        $Country: String!,
        $ContactEmail: String!,
        $Verified: Boolean!,
        $VerificationNotes: String!,
        $Active: Boolean!,
        $LicenseType: String!,
        $SlackWebhookUrl: String!,
        ) {
        createOrganization(
            OrganizationId: $OrganizationId,
            OrganizationName: $OrganizationName,
            Address1: $Address1,
            Address2: $Address2,
            City: $City,
            State: $State,
            PostalCode: $PostalCode,
            Country: $Country,
            ContactEmail: $ContactEmail,
            Verified: $Verified,
            VerificationNotes: $VerificationNotes,
            Active: $Active,
            LicenseType: $LicenseType,
            SlackWebhookUrl: $SlackWebhookUrl) {
            OrganizationId
            OrganizationName
            Address1
            Address2
            City
            State
            PostalCode
            Country
            ContactEmail
            Verified
            Active
            PublicKey
            VerificationNotes
            LicenseType
            SlackWebhookUrl
        }
    }
`;

const deleteOrganizationMutation = gql`
    mutation deleteOrganization(
        $OrganizationId: String!) {
        deleteOrganization(
            OrganizationId: $OrganizationId,
        ) {
            OrganizationId
        }
    }
`;

const addGrantorMutation = gql`
    mutation addGrantor($OrganizationId: String!, $Email: String!) {
        addGrantor(OrganizationId: $OrganizationId, Email: $Email) {
            OrganizationId
            UserName
        }
    }
`;

const deleteGrantorMutation = gql`
    mutation deleteGrantor(
        $UserName: String!,
        $OrganizationId: String!) {
        deleteGrantor(
            UserName: $UserName,
            OrganizationId: $OrganizationId) {
            UserName
            OrganizationId
        }
    }
`;

const deleteFutureMinterMutation = gql`
    mutation deleteGrantor(
        $Email: String!,
        $OrganizationId: String!) {
        deleteFutureMinter(
            Email: $Email,
            OrganizationId: $OrganizationId) {
            Email
            OrganizationId
        }
    }
`;

const addOrgAdminMutation = gql`
    mutation addOrgAdmin($OrganizationId: String!, $Email: String!) {
        addOrgAdmin(OrganizationId: $OrganizationId, Email: $Email) {
            OrganizationId
            UserName
        }
    }
`;

const deleteOrgAdminMutation = gql`
    mutation deleteOrgAdmin(
        $UserName: String!,
        $OrganizationId: String!) {
        deleteOrgAdmin(
            UserName: $UserName,
            OrganizationId: $OrganizationId) {
            UserName
            OrganizationId
        }
    }
`;

const grantorOrgsQuery = gql`
    query getGrantors(
        $UserName: String!) {
        getGrantorOrgsFromUserName(UserName: $UserName) {
            OrganizationId
            OrganizationName
            Address1
            Address2
            City
            State
            PostalCode
            Country
            ContactEmail
            PublicKey
            Verified
            Active
            Api {
                ApiKey
            }
        }
    }
`;

function useGrantorOrgsQuery(userName: string) {
    return useQuery<GetGrantorOrgsResponse>(
        grantorOrgsQuery,
        {
            variables: {
                UserName: userName,
            },
            fetchPolicy: 'cache-and-network',
        },
    );
}

const adminsForOrgQuery = gql`
    query getAdminsForOrg(
        $OrganizationId: String!) {
        getAdminsForOrg(OrganizationId: $OrganizationId) {
            OrganizationId
            UserName
            User {
                UserName
                GivenName
                FamilyName
                Email
            }
        }
    }
`;

function useAdminsForOrgQuery(organizationId: string) {
    return useQuery<GetAdminsForOrgResponse>(
        adminsForOrgQuery,
        {
            variables: {
                OrganizationId: organizationId,
            },
        },
    );
}

const futureMintersForOrgQuery = gql`
    query getFutureMintersForOrg(
        $OrganizationId: String!) {
        getFutureMintersForOrg(OrganizationId: $OrganizationId) {
            OrganizationId
            Email
            AdminGivenName
            AdminFamilyName
        }
    }
`;

function useFutureMintersForOrgQuery(organizationId: string) {
    return useQuery<GetFutureMintersForOrgResponse>(
        futureMintersForOrgQuery,
        {
            variables: {
                OrganizationId: organizationId,
            },
        },
    );
}

const grantorsForOrgQuery = gql`
    query getGrantorsForOrg(
        $OrganizationId: String!) {
        getGrantorsForOrg(OrganizationId: $OrganizationId) {
            OrganizationId
            UserName
            User {
                UserName
                GivenName
                FamilyName
                Email
            }
        }
    }
`;

function useGrantorsForOrgQuery(organizationId: string) {
    return useQuery<GetGrantorsForOrgResponse>(
        grantorsForOrgQuery,
        {
            variables: {
                OrganizationId: organizationId,
            },
        },
    );
}

const grantorsQuery = gql`
    query getGrantors(
        $UserName: String!) {
        getGrantorsFromUserName(UserName: $UserName) {
            UserName
            OrganizationId
            Organization {
                OrganizationId
                OrganizationName
            }
        }
    }
`;

function useGrantorsQuery(userName: string) {
    return useQuery<GetGrantorsResponse>(
        grantorsQuery,
        {
            variables: {
                UserName: userName,
            },
        },
    );
}

const isAdminQuery = gql`
    query isAdmin(
        $UserName: String!) {
        isAdmin(UserName: $UserName) {
            UserName
            IsAnAdmin
        }
    }
`;

function useAdminQuery(userName: string, skip = false) {
    return useQuery<IsAdminResponse>(
        isAdminQuery,
        {
            variables: {
                UserName: userName,
            },
            skip,
        },
    );
}

const orgAdminsQuery = gql`
    query getOrgAdminsFromUserName(
        $UserName: String!) {
        getOrgAdminsFromUserName(UserName: $UserName) {
            UserName
            OrganizationId
            Organization {
                OrganizationId
                OrganizationName
                Api {
                    ApiKey
                }
            }
        }
    }
`;

function useOrgAdminsQuery(userName: string) {
    return useQuery<GetOrgAdminsResponse>(
        orgAdminsQuery,
        {
            variables: {
                UserName: userName,
            },
        },
    );
}

const organizationQuery = gql`
    query getOrganization(
        $OrganizationId: String!) {
        getOrganizationFromId(OrganizationId: $OrganizationId) {
            OrganizationName
            OrganizationId
            Address1
            Address2
            City
            State
            PostalCode
            Country
            ContactEmail
            PublicKey
            Verified
            Active
            VerificationNotes
            LicenseType
            SlackWebhookUrl
        }
    }
`;

function useOrganizationQuery(organizationId: string, skip = false) {
    return useQuery<GetOrganizationResponse>(
        organizationQuery,
        {
            variables: {
                OrganizationId: organizationId,
            },
            skip,
        },
    );
}

const organizationsQuery = gql`
    query getOrganizations($UserName: String!) {
        getOrganizations(UserName: $UserName) {
            OrganizationName
            OrganizationId
            Address1
            Address2
            City
            State
            PostalCode
            Country
            ContactEmail
            PublicKey
            Verified
            Active
            VerificationNotes
            LicenseType
            SlackWebhookUrl
        }
    }
`;

function useOrganizationsQuery(userName: string) {
    return useQuery<GetOrganizationsResponse>(
        organizationsQuery, {
            variables: {
                UserName: userName,
            },
        },
    );
}

export {
    grantorOrgsQuery,
    adminsForOrgQuery,
    futureMintersForOrgQuery,
    grantorsForOrgQuery,
    grantorsQuery,
    isAdminQuery,
    orgAdminsQuery,
    organizationQuery,
    organizationsQuery,
    createOrganizationMutation,
    deleteOrganizationMutation,
    addGrantorMutation,
    deleteGrantorMutation,
    deleteFutureMinterMutation,
    addOrgAdminMutation,
    deleteOrgAdminMutation,
    useGrantorOrgsQuery,
    useAdminsForOrgQuery,
    useFutureMintersForOrgQuery,
    useGrantorsForOrgQuery,
    useGrantorsQuery,
    useAdminQuery,
    useOrgAdminsQuery,
    useOrganizationQuery,
    useOrganizationsQuery,
};
